// =============================================================================
// components/ErrorBoundary.js — Beyaz Sayfa Önleme + Teşhis (2026-05-10)
// =============================================================================
// React component'te runtime hata olursa varsayılan davranış: tüm tree çöker,
// kullanıcı beyaz ekran görür. Bu boundary:
//   1. Hatayı yakalar
//   2. Kullanıcıya açıklayıcı mesaj + sebep + stack gösterir
//   3. "Yenile" / "Anasayfaya Dön" butonu sunar
//   4. ChunkLoadError ise otomatik sayfayı tekrar yükler (cache mismatch)
//   5. Hatayı server'a POST eder (services/error-logger.js mevcut)
// =============================================================================

import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reloadAttempted: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // ChunkLoadError = browser eski main.js'i çekti, yeni chunk'lar 404.
    // Bir kez otomatik hard reload dene (sessionStorage flag ile loop önle).
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error?.message || '') ||
      /Loading CSS chunk/.test(error?.message || '');

    if (isChunkError && !sessionStorage.getItem('__chunk_reload_attempted')) {
      sessionStorage.setItem('__chunk_reload_attempted', '1');
      window.location.reload();
      return;
    }

    // Server'a hata logla (best effort, fail sessizce)
    try {
      const token = localStorage.getItem('token');
      if (token) {
        fetch('https://api.miksanplastik.com.tr/api/admin/system/log-client-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            message: String(error?.message || error),
            stack: String(error?.stack || ''),
            component_stack: String(errorInfo?.componentStack || ''),
            url: window.location.href,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString()
          })
        }).catch(() => {});
      }
    } catch (_) {}

    // Browser console'a da düşür — devtools açıksa debug için
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    sessionStorage.removeItem('__chunk_reload_attempted');
    window.location.reload();
  };

  handleGoHome = () => {
    sessionStorage.removeItem('__chunk_reload_attempted');
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const error = this.state.error;
    const stack = String(error?.stack || '').split('\n').slice(0, 6).join('\n');
    const componentStack = String(this.state.errorInfo?.componentStack || '')
      .split('\n').slice(0, 6).join('\n');

    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }}>
        <div style={{
          maxWidth: 640, background: 'white', borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)', overflow: 'hidden'
        }}>
          <div style={{ background: '#dc2626', color: 'white', padding: '20px 24px' }}>
            <div style={{ fontSize: 14, opacity: 0.9, letterSpacing: 1, textTransform: 'uppercase' }}>
              Mik-San Admin Panel
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>
              ⚠️ Bir hata oluştu
            </div>
          </div>

          <div style={{ padding: 24 }}>
            <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
              Sayfa yüklenirken beklenmeyen bir sorun çıktı. Aşağıda hata mesajı var —
              tekrarlanırsa lütfen mesajı bana iletin.
            </p>

            <div style={{
              marginTop: 16, padding: 12, background: '#fef2f2', borderRadius: 6,
              borderLeft: '4px solid #dc2626', fontFamily: 'monospace', fontSize: 13,
              color: '#991b1b', wordBreak: 'break-word'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{error?.name || 'Error'}:</div>
              <div>{error?.message || String(error)}</div>
            </div>

            {stack && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: 13 }}>
                  📋 Teknik detay (stack trace)
                </summary>
                <pre style={{
                  marginTop: 8, padding: 10, background: '#f1f5f9', borderRadius: 4,
                  fontSize: 11, color: '#334155', overflow: 'auto', maxHeight: 200,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}>{stack}</pre>
                {componentStack && (
                  <pre style={{
                    marginTop: 4, padding: 10, background: '#f1f5f9', borderRadius: 4,
                    fontSize: 11, color: '#334155', overflow: 'auto', maxHeight: 200,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>{componentStack}</pre>
                )}
              </details>
            )}

            <div style={{
              marginTop: 20, padding: 10, background: '#eff6ff', borderRadius: 6,
              fontSize: 13, color: '#1e40af'
            }}>
              💡 <strong>İpucu:</strong> Bu mesajı bana iletirseniz tam olarak ne olduğunu görüp düzeltebilirim.
              Hata otomatik olarak server'a kaydedildi.
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <button onClick={this.handleReload} style={{
                flex: 1, padding: '12px 20px', background: '#1565C0', color: 'white',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer'
              }}>
                🔄 Sayfayı Yenile
              </button>
              <button onClick={this.handleGoHome} style={{
                flex: 1, padding: '12px 20px', background: 'white', color: '#1565C0',
                border: '2px solid #1565C0', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer'
              }}>
                🏠 Anasayfaya Dön
              </button>
            </div>

            <div style={{ marginTop: 16, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              {new Date().toISOString()} · {window.location.pathname}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
